import type { ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/shared-ui/atoms';
import { Accordion } from '@/shared-ui/molecules/accordion';
import { isExclusiveAttribute } from '../../../lib/refinements';

type ProductSearchRefinement = ShopperSearch.schemas['ProductSearchRefinement'];

export interface FilterGroupProps {
    refinement: ProductSearchRefinement;
    isSelected: (attributeId: string, value: string) => boolean;
    onToggle: (attributeId: string, value: string) => void;
    className?: string;
}

/** Single-select groups (price) keep the checkbox look but expose radio semantics. */
export const FilterGroup = ({ refinement, isSelected, onToggle, className }: FilterGroupProps) => {
    const { attributeId, label, values = [] } = refinement;
    const exclusive = isExclusiveAttribute(attributeId);

    return (
        <Accordion.Item
            value={attributeId}
            data-slot="filter-group"
            className={cn('border-snk-greige data-[state=closed]:border-transparent', className)}>
            <Accordion.Trigger className="py-4">{label || attributeId}</Accordion.Trigger>
            <Accordion.Content accordionContentClassName="pb-4">
                <ul
                    role={exclusive ? 'radiogroup' : undefined}
                    aria-label={label || attributeId}
                    className="flex max-h-[248px] flex-col gap-2 overflow-y-auto [scrollbar-color:var(--color-snk-greige)_transparent] [scrollbar-width:thin]">
                    {values.map((option) => {
                        const text = option.hitCount != null ? `${option.label} (${option.hitCount})` : option.label;
                        return (
                            <li key={option.value} role={exclusive ? 'none' : undefined}>
                                <Checkbox.Root>
                                    <Checkbox.Content
                                        {...(exclusive && { role: 'radio' })}
                                        checked={isSelected(attributeId, option.value)}
                                        onCheckedChange={() => onToggle(attributeId, option.value)}
                                    />
                                    <Checkbox.Label className="text-base leading-5 tracking-[0.16px]">
                                        {text}
                                    </Checkbox.Label>
                                </Checkbox.Root>
                            </li>
                        );
                    })}
                </ul>
            </Accordion.Content>
        </Accordion.Item>
    );
};
